/**
 * RadioFullscreenOverlay — CarPlay-style "Now Playing" takeover.
 *
 * Big blurred-artwork backdrop, reactive waveform layered behind a clean
 * centered artwork tile, marquee title, prominent m:ss progress, and a
 * horizontally-swipeable station picker at the bottom. All audio state
 * is owned by the parent SyncedRadioPlayer — this component is purely
 * presentational + dispatches callbacks (toggle play, change station,
 * mute, share, close).
 *
 * Renders via React Portal to <body> so it always sits above the layout
 * and ignores parent overflow / transforms.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  X, Play, Pause, Volume2, VolumeX, Share2, Radio,
  ShoppingBag, ArrowRight, ChevronLeft, ChevronRight, Sparkles
} from 'lucide-react';
import AnimatedWaveform from './AnimatedWaveform';
import { MarqueeText } from './MarqueeText';

const fmt = (sec) => {
  if (!sec && sec !== 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export function RadioFullscreenOverlay({
  open,
  onClose,
  // Track state
  track,
  next,
  pos,
  progressPct,
  playing,
  muted,
  volume,
  awaitingUnmute,
  // Station state
  station,
  stations = [],
  onStationChange,
  // Theme
  theme,
  // Capabilities
  locked,
  adminOverride,
  shareable,
  // Callbacks
  onTogglePlay,
  onToggleMute,
  onUnmute,
  onVolumeChange,
  onShare,
  // Where to navigate on Buy CTA
  buyHref,
  buyLabel,
}) {
  const scrollerRef = useRef(null);

  // Esc to close + arrow keys to switch station + space to toggle play
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const idx = stations.findIndex((s) => s.key === station);
        if (idx === -1) return;
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const next = stations[(idx + dir + stations.length) % stations.length];
        onStationChange?.(next.key);
      }
      if (e.code === 'Space') {
        e.preventDefault();
        if (!(playing && locked && !adminOverride)) onTogglePlay?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, stations, station, onStationChange, playing, locked, adminOverride, onTogglePlay]);

  // Lock body scroll while overlay is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Auto-scroll active station card into view
  useEffect(() => {
    if (!open) return;
    const root = scrollerRef.current;
    if (!root) return;
    const activeEl = root.querySelector(`[data-station-card="${station}"]`);
    if (activeEl && activeEl.scrollIntoView) {
      activeEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [open, station]);

  if (!open) return null;

  const artworkUrl = track?.artwork
    ? (track.artwork.startsWith('http') ? track.artwork : `${process.env.REACT_APP_BACKEND_URL}/api/artwork/${track.artwork}`)
    : null;

  const canPause = !(playing && locked && !adminOverride);

  const overlay = (
    <div
      className="preserve-dark fixed inset-0 z-[9999] flex flex-col"
      data-preserve-dark="true"
      data-testid="radio-fullscreen-overlay"
      role="dialog"
      aria-label="Now playing — fullscreen"
      aria-modal="true"
    >
      {/* Single-instance keyframes used by the overlay */}
      <style>{`
        @keyframes rfx-fade { from{opacity:0;} to{opacity:1;} }
        @keyframes rfx-zoom { from{opacity:0; transform: scale(0.94);} to{opacity:1; transform: scale(1);} }
        @keyframes rfx-pulse-bg { 0%,100%{opacity:.55;} 50%{opacity:.85;} }
        @keyframes rfx-vinyl { from{transform:rotate(0deg);} to{transform:rotate(360deg);} }
        .rfx-no-scrollbar::-webkit-scrollbar { display: none; }
        .rfx-no-scrollbar { scrollbar-width: none; }
      `}</style>

      {/* — Backdrop layer 1: ginormous blurred artwork — */}
      {artworkUrl ? (
        <img
          src={artworkUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: 'blur(48px) saturate(140%)',
            transform: 'scale(1.15)',
            opacity: 0.6,
            animation: playing ? 'rfx-pulse-bg 6s ease-in-out infinite' : undefined,
          }}
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at 30% 30%, ${theme.primary}33, transparent 55%), radial-gradient(ellipse at 70% 70%, ${theme.secondary}33, transparent 55%), #000`,
          }}
        />
      )}
      {/* Layer 2: dark wash so foreground stays legible */}
      <div className="absolute inset-0 bg-black/70" />
      {/* Layer 3: subtle noise / grill for tactility */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1.5px)',
          backgroundSize: '8px 8px',
        }}
      />
      {/* Layer 4: ambient corner glows */}
      <div
        className="absolute -top-32 -left-32 w-[40vw] h-[40vw] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${theme.primary}55, transparent 70%)`, filter: 'blur(60px)' }}
      />
      <div
        className="absolute -bottom-32 -right-32 w-[45vw] h-[45vw] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${theme.secondary}55, transparent 70%)`, filter: 'blur(70px)' }}
      />

      {/* Layer 5: fullscreen reactive waveform behind the artwork */}
      <div
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 px-4 sm:px-12 pointer-events-none opacity-30"
        aria-hidden="true"
      >
        <AnimatedWaveform
          playing={playing && !muted}
          accent={theme.accent || 'gold'}
          height={240}
        />
      </div>

      {/* — Top bar: station label + close — */}
      <header
        className="relative z-10 flex items-center justify-between px-4 sm:px-8 py-4"
        style={{ animation: 'rfx-fade 320ms ease-out both' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Radio size={16} style={{ color: theme.primary }} />
          <span
            className="font-mono uppercase tracking-[0.3em] text-xs sm:text-sm truncate"
            style={{ color: theme.primary }}
          >
            One Voice · {theme.name} Radio
          </span>
          {playing && (
            <span
              className="ml-1 w-2 h-2 rounded-full animate-pulse"
              style={{ background: theme.primary, boxShadow: `0 0 12px ${theme.primary}` }}
              aria-hidden="true"
            />
          )}
        </div>
        <button
          onClick={onClose}
          aria-label="Close fullscreen"
          data-testid="radio-fullscreen-close"
          className="rounded-full p-2 text-white/80 hover:text-white hover:bg-white/10 transition-all hover:scale-110 active:scale-95"
        >
          <X size={22} />
        </button>
      </header>

      {/* Autoplay-muted prompt */}
      {awaitingUnmute && (
        <button
          type="button"
          onClick={onUnmute}
          className="relative z-10 mx-auto mb-2 px-4 py-2 bg-gradient-to-r from-[#FFDA33] to-[#FFE97A] text-black flex items-center justify-center gap-2 font-mono uppercase tracking-widest text-xs hover:brightness-110 rounded-full"
          data-testid="radio-fullscreen-tap-unmute"
        >
          <VolumeX size={14} /> Live — tap to unmute
        </button>
      )}

      {/* — Main "Now Playing" stage — */}
      <main
        className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-8 min-h-0"
        style={{ animation: 'rfx-zoom 420ms cubic-bezier(0.2,0.8,0.2,1) both' }}
      >
        {/* Big artwork */}
        <div
          className="relative shrink-0"
          style={{
            width: 'min(62vh, 360px)',
            height: 'min(62vh, 360px)',
          }}
        >
          {/* glow ring */}
          <div
            className="absolute -inset-4 rounded-3xl pointer-events-none"
            style={{
              background: `linear-gradient(135deg, ${theme.primary}, transparent 60%, ${theme.secondary})`,
              filter: 'blur(28px)',
              opacity: playing ? 0.7 : 0.35,
              transition: 'opacity 600ms ease',
            }}
          />
          {artworkUrl ? (
            <img
              src={artworkUrl}
              alt={track?.title || 'Now playing'}
              className="relative w-full h-full object-cover rounded-2xl shadow-2xl"
              style={{
                boxShadow: `0 30px 60px rgba(0,0,0,0.7), 0 0 60px ${theme.primary}55`,
                transform: playing ? 'scale(1.0)' : 'scale(0.97)',
                transition: 'transform 700ms cubic-bezier(0.2,0.8,0.2,1)',
              }}
              data-testid="radio-fullscreen-artwork"
            />
          ) : (
            <div
              className="relative w-full h-full rounded-2xl flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${theme.primary}33, #111)`,
                boxShadow: `0 30px 60px rgba(0,0,0,0.7), 0 0 60px ${theme.primary}55`,
              }}
            >
              <Radio size={96} style={{ color: theme.primary }} />
            </div>
          )}
        </div>

        {/* Title + artist */}
        <div className="mt-6 sm:mt-8 max-w-2xl w-full text-center">
          <MarqueeText
            text={track?.title || 'Tune in…'}
            speedPxPerSec={50}
            className="font-['Chakra_Petch'] font-extrabold uppercase tracking-wide text-3xl sm:text-5xl leading-tight"
            style={{
              color: '#fff',
              textShadow: `0 0 22px ${theme.primary}, 0 0 38px ${theme.glow}, 0 4px 8px rgba(0,0,0,0.85)`,
              WebkitTextStroke: `0.5px ${theme.primary}66`,
            }}
            data-testid="radio-fullscreen-title"
          />
          <MarqueeText
            text={track?.artist || '—'}
            speedPxPerSec={40}
            className="mt-2 font-['Chakra_Petch'] font-bold text-lg sm:text-2xl"
            style={{
              color: theme.primary,
              textShadow: `0 0 14px ${theme.glow}, 0 2px 6px rgba(0,0,0,0.85)`,
            }}
          />

          {/* Buy CTA — keep accessible from fullscreen too */}
          {buyHref && track?.id && (
            <Link
              to={buyHref}
              onClick={onClose}
              className="mt-3 inline-flex items-center gap-1.5 text-xs sm:text-sm font-mono uppercase tracking-widest text-[#00ff88] hover:text-white hover:bg-[#00ff88]/10 px-3 py-1.5 rounded-full border border-[#00ff88]/40 transition-colors"
              data-testid="radio-fullscreen-buy-cta"
            >
              <ShoppingBag size={14} /> {buyLabel || 'Buy this track'} <ArrowRight size={14} />
            </Link>
          )}
        </div>

        {/* Progress bar */}
        <div className="mt-6 w-full max-w-2xl">
          <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden border border-[#222] shadow-inner">
            <div
              className="h-full rounded-full transition-[width] duration-500 ease-linear"
              style={{
                width: `${progressPct || 0}%`,
                background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`,
                boxShadow: `0 0 14px ${theme.glow}`,
              }}
              data-testid="radio-fullscreen-progress-fill"
            />
          </div>
          <div className="flex justify-between items-center mt-2 font-mono tabular-nums">
            <span className="text-base font-bold text-white" data-testid="radio-fullscreen-current-time">
              {fmt(pos)}
            </span>
            <span className="text-base font-medium text-white/70" data-testid="radio-fullscreen-total-time">
              {fmt(track?.duration || 0)}
            </span>
          </div>
        </div>

        {/* Controls row */}
        <div className="mt-6 flex items-center gap-5 sm:gap-7">
          {/* Mute */}
          <button
            onClick={onToggleMute}
            className="text-white/80 hover:text-white p-3 rounded-full transition-all hover:bg-white/10 hover:scale-110 active:scale-95"
            aria-label={muted ? 'Unmute' : 'Mute'}
            data-testid="radio-fullscreen-mute"
          >
            {muted ? <VolumeX size={22} /> : <Volume2 size={22} />}
          </button>
          {/* Big Play / Pause */}
          <div className="relative">
            {playing && (
              <span
                aria-hidden
                className="absolute inset-0 rounded-full animate-ping pointer-events-none"
                style={{ background: `${theme.primary}40`, animationDuration: '2.4s' }}
              />
            )}
            <button
              onClick={onTogglePlay}
              disabled={!canPause}
              className={
                "relative w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center text-black transition-all duration-200 ease-out " +
                (canPause
                  ? "hover:scale-110 hover:shadow-2xl active:scale-95"
                  : "opacity-60 cursor-not-allowed")
              }
              style={{
                background: theme.primary,
                boxShadow: `0 0 36px ${theme.primary}80, 0 12px 32px rgba(0,0,0,0.5)`,
              }}
              title={!canPause ? "Live continuous stream — use the speaker icon to mute." : ""}
              data-testid="radio-fullscreen-toggle-play"
              aria-label={playing ? 'Pause' : 'Play'}
            >
              {playing ? <Pause size={36} /> : <Play size={36} className="ml-1" />}
            </button>
          </div>
          {/* Volume */}
          <div className="hidden sm:flex items-center gap-2">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={muted ? 0 : volume}
              onChange={(e) => onVolumeChange?.(parseFloat(e.target.value))}
              className="w-28 accent-white"
              data-testid="radio-fullscreen-volume"
              aria-label="Volume"
            />
          </div>
          {/* Share */}
          {shareable && (
            <button
              onClick={onShare}
              className="text-white/80 hover:text-white p-3 rounded-full transition-all hover:bg-white/10 hover:scale-110 active:scale-95"
              aria-label="Share this radio"
              data-testid="radio-fullscreen-share"
            >
              <Share2 size={22} />
            </button>
          )}
        </div>

        {/* Up next */}
        {next && (
          <div className="mt-4 text-center text-white/60 font-mono text-xs sm:text-sm">
            <Sparkles size={12} className="inline mr-1.5" style={{ color: theme.primary }} />
            Up next: <span className="text-white/90">{next.title}</span> — <span className="text-white/70">{next.artist}</span>
          </div>
        )}

        {locked && !adminOverride && (
          <div className="mt-2 text-[11px] font-mono text-white/55 flex items-center gap-1.5">
            <Radio size={11} style={{ color: theme.primary }} />
            Live 24/7 continuous stream · controls limited to mute / volume to keep every listener in sync
          </div>
        )}
      </main>

      {/* — Bottom: swipeable station picker — */}
      <footer className="relative z-10 pb-6 pt-3 px-2 sm:px-6">
        <div className="flex items-center justify-between mb-2 px-2 sm:px-3">
          <span className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs text-white/60">
            Stations
          </span>
          <span className="hidden sm:inline font-mono text-[10px] text-white/40">
            ← / → keys · drag to switch
          </span>
        </div>
        <div className="relative">
          {/* fade edges */}
          <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-black/80 to-transparent pointer-events-none z-10" />
          <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-black/80 to-transparent pointer-events-none z-10" />
          <div
            ref={scrollerRef}
            className="flex gap-3 sm:gap-4 overflow-x-auto rfx-no-scrollbar snap-x snap-mandatory px-2 sm:px-4 pb-1"
            data-testid="radio-fullscreen-station-picker"
          >
            {stations.map((s) => {
              const Icon = s.icon;
              const isActive = s.key === station;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => onStationChange?.(s.key)}
                  data-station-card={s.key}
                  data-testid={`radio-fullscreen-station-${s.key}`}
                  className={
                    "snap-center shrink-0 group relative rounded-2xl px-5 py-4 sm:px-7 sm:py-5 min-w-[180px] sm:min-w-[220px] " +
                    "border transition-all duration-200 ease-out text-left "
                  }
                  style={{
                    borderColor: isActive ? `${s.accent}` : 'rgba(255,255,255,0.08)',
                    background: isActive
                      ? `linear-gradient(135deg, ${s.accent}26, ${s.accent}10)`
                      : 'rgba(255,255,255,0.04)',
                    boxShadow: isActive
                      ? `0 0 24px ${s.accent}55, inset 0 0 0 1px ${s.accent}88`
                      : 'inset 0 0 0 1px rgba(255,255,255,0.04)',
                    transform: isActive ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={16} style={{ color: isActive ? s.accent : 'rgba(255,255,255,0.6)' }} />
                    <span
                      className="font-mono uppercase tracking-widest text-[10px]"
                      style={{ color: isActive ? s.accent : 'rgba(255,255,255,0.5)' }}
                    >
                      {isActive ? 'Now playing' : 'Tap to switch'}
                    </span>
                    {isActive && (
                      <span
                        className="ml-auto w-1.5 h-1.5 rounded-full animate-pulse"
                        style={{ background: s.accent, boxShadow: `0 0 8px ${s.accent}` }}
                      />
                    )}
                  </div>
                  <div
                    className="font-['Chakra_Petch'] font-bold uppercase tracking-wide text-base sm:text-lg leading-tight"
                    style={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.85)' }}
                  >
                    {s.label}
                  </div>
                  {s.blurb && (
                    <div className="mt-1 font-mono text-[10px] sm:text-[11px] text-white/55 line-clamp-2">
                      {s.blurb}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tiny prev/next chevrons (mobile-friendly) */}
        <div className="flex items-center justify-center gap-2 mt-3 sm:hidden">
          {stations.map((s) => (
            <span
              key={s.key}
              className="w-1.5 h-1.5 rounded-full transition-all"
              style={{
                background: s.key === station ? s.accent : 'rgba(255,255,255,0.25)',
                width: s.key === station ? 16 : 6,
              }}
            />
          ))}
        </div>

        {/* Desktop arrows for accessibility */}
        <div className="hidden sm:flex items-center justify-center gap-3 mt-2">
          <button
            onClick={() => {
              const i = stations.findIndex((s) => s.key === station);
              if (i === -1) return;
              onStationChange?.(stations[(i - 1 + stations.length) % stations.length].key);
            }}
            className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-all"
            aria-label="Previous station"
            data-testid="radio-fullscreen-prev-station"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => {
              const i = stations.findIndex((s) => s.key === station);
              if (i === -1) return;
              onStationChange?.(stations[(i + 1) % stations.length].key);
            }}
            className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-all"
            aria-label="Next station"
            data-testid="radio-fullscreen-next-station"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </footer>
    </div>
  );

  return createPortal(overlay, document.body);
}

export default RadioFullscreenOverlay;
